import React, { FC, useState, useEffect, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import { maxLength } from '../../../utils/validate'
import { useTranslation } from 'react-i18next'
import { replaceAndStrip } from '../../../utils/string'
import Modal from '../common/Modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AddMemberModule from './AddMemberModule'
import { sortBy } from 'lodash'

import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import { arrayMove, SortableContext, rectSortingStrategy } from '@dnd-kit/sortable'
import Grid from './Grid'
import SortableItem from './SortableItem'
import { compile } from '../../../utils/search'

interface MemberDetail {
  member_id: number
  name: string
  kana: string | null
  profile_image: string | null
}

interface Errors {
  name: string[]
  flow: string[]
  member: string[]
}

interface Flow extends MemberDetail {
  priority: number
  item_id: number
}

interface ApprovalGroup {
  approval_group_id: number
  name: string
  description: string | null
  created_by: string | null
  approvers: {
    member_id: number
    priority: number
    name: string
    kana: string | null
    profile_image: string | null
  }[]
  members: MemberDetail[]
}

interface ModalState {
  display: boolean,
  type: string,
  id: number | undefined,
  targetData: ApprovalGroup | null
  memberList: MemberDetail[]
  alreadyAssignedMemberIds: number[]
}

interface CreateParams {
  name: string
  description: string | null
  approvers: {
    member_id: number
    priority: number
  }[]
  member_ids: number[]
}

interface UpdateParams {
  id: number
  name?: string
  description?: string | null
  approvers?: {
    member_id: number
    priority: number
  }[]
  member_ids?: number[]
}

interface Props {
  modalState: ModalState
  closeCallback: VoidFunction
  createCallback: (params: CreateParams, diff: number[]) => void
  updateCallback: (params: UpdateParams, diff: number[]) => void
}

const ApproverSettingModal: FC<Props> = (props) => {
  const { t } = useTranslation()

  const {
    commonPart,
  } = useSelector((state: RootState) => state)

  const {
    modalState,
    closeCallback,
    createCallback,
    updateCallback,
  } = props

  const initialInput = {
    name: "",
    description: null,
    approvers: [],
    members: [],
  }

  const initErrors = {
    name: [],
    flow: [],
    member: [],
  }

  const initFlow: Flow = {
    priority: 0,
    item_id: 0,
    member_id: 0,
    name: "",
    kana: null,
    profile_image: null,
  }

  const [groupName, setGroupName] = useState<string>("")

  const [displayModal, setDisplayModal] = useState<boolean>(false)
  const [errors, setErrors] = useState<Errors>(initErrors)

  const [selectMembers, setSelectMembers] = useState<MemberDetail[]>([])
  const [otherAssignedMembers, setOtherAssignedMembers] = useState<MemberDetail[]>([])
  const [flow, setFlow] = useState<Flow[]>([])

  const [isMoving, setIsMoving] = useState<boolean>(true)

  const [displaySelectBoxNum, setDisplaySelectBoxNum] = useState<number | null>(null)

  const [searchMemberWord, setSearchMemberWord] = useState<string>("")

  const [approverMembers, setApproverMembers] = useState<string>("")

  const [isDisplayConfirmModal, setIsDisplayConfirmModal] = useState<boolean>(false)
  const [isConfirm, setIsConfirm] = useState<boolean>(false)

  const filterMemberList = useMemo(() => {
    if (!searchMemberWord) return modalState.memberList
    const searchWordArray = compile(searchMemberWord.toLocaleLowerCase().split(/\s+/))
    return modalState.memberList.filter((v) => searchWordArray.test(v.name.toLocaleLowerCase() + v.kana))
  }, [searchMemberWord, modalState.memberList])

  const clear = () => {
    closeCallback()
    setTimeout(() => {
      setGroupName("")
      setSelectMembers([])
      setFlow([])
      setSearchMemberWord("")
    }, 500)
  }

  const replaceAndStripName = (event: React.FocusEvent<HTMLInputElement>) => {
    setGroupName(replaceAndStrip(event.target.value))
    if (event.target.value) {
      setErrors({
        ...errors,
        name: [],
      })
    }
  }

  const setMember = (members: MemberDetail[]) => {
    setSelectMembers(members)
  }

  const delMember = (memberId: number) => {
    setSelectMembers([...selectMembers.filter((v) => v.member_id !== memberId)])
  }

  const numArrayDiff = (arr1: number[], arr2: number[]) => {
    const diff1 = arr1.filter((v) => arr2.indexOf(v) === -1)
    const diff2 = arr2.filter((v) => arr1.indexOf(v) === -1)
    return [...diff1, ...diff2]
  }

  const diffApprovers = () => {
    const originApprovers = modalState.targetData?.approvers || []
    const diffApproverMembers = numArrayDiff(flow.map((v) => v.member_id), originApprovers.map((v) => v.member_id))
    if (diffApproverMembers.length) return true
    return flow.some((v) => originApprovers.some((w) => v.member_id === w.member_id && v.priority !== w.priority))
  }

  const saveApprovalGroup = () => {
    if (!commonPart.company?.id) return
    const tmpErrors: Errors = initErrors
    let errorFlg = false

    // name
    if (groupName === '') {
      tmpErrors.name.push(t('form.error.no_team_name_error'))
      errorFlg = true
    }
    const nameLength = maxLength(30)(groupName)
    if (nameLength) {
      tmpErrors.name.push(t(nameLength, { length: 30 }))
      errorFlg = true
    }
    setErrors({
      name: tmpErrors.name,
      flow: tmpErrors.flow,
      member: tmpErrors.member,
    })
    if (errorFlg) return
    if (modalState.type === "create") {
      const tmpValues:CreateParams = {
        name: groupName,
        description: null,
        approvers: flow.map((v) => ({ member_id: v.member_id, priority: v.priority })),
        member_ids: selectMembers.map((v) => v.member_id),
      }
      createCallback(tmpValues, otherAssignedMembers.map((v) => v.member_id))
    } else if (modalState.type === "update" && modalState.id) {
      let tmpValues:UpdateParams = {
        id: modalState.id,
      }
      if (diffApprovers()) {
        tmpValues = {
          ...tmpValues,
          approvers: flow.map((v) => ({ member_id: v.member_id, priority: v.priority })),
        }
      }
      const diffMembers = numArrayDiff(selectMembers.map((v) => v.member_id), modalState.targetData?.members.map((w) => w.member_id) || [])
      if (diffMembers.length) {
        tmpValues = {
          ...tmpValues,
          member_ids: selectMembers.map((v) => v.member_id),
        }
      }
      if ((modalState.targetData?.name || initialInput.name) !== groupName) {
        tmpValues = {
          ...tmpValues,
          name: groupName,
        }
      }
      updateCallback(tmpValues, otherAssignedMembers.map((v) => v.member_id))
    }
    setIsDisplayConfirmModal(false)
    setIsConfirm(false)
  }

  const addFlow = () => {
    let addValues = {
      ...initFlow,
    }
    if (flow.length) {
      addValues = { ...addValues, item_id: Math.max(...flow.map((v) => v.item_id)) + 1 }
    } else {
      addValues = { ...addValues, item_id: 0 }
    }
    setFlow([
      ...flow.map((v) => ({ ...v, priority: v.priority + 1 })),
      addValues,
    ])
  }

  const delFlow = (itemId: number) => {
    const tmpLength = flow.length - 2
    setFlow(flow.filter((v) => v.item_id !== itemId).map((v, i) => ({ ...v, priority: tmpLength - i })))
  }

  const closeApproverList = () => {
    setDisplaySelectBoxNum(null)
    setSearchMemberWord("")
  }

  const selectApprover = (selectMemberId: number, itemId: number) => {
    const selectMember = modalState.memberList.find((v) => v.member_id === selectMemberId)
    if (!selectMember) return
    setFlow(
      flow.map((v) => (v.item_id === itemId ? { ...v, member_id: selectMember.member_id, name: selectMember.name, kana: selectMember.kana } : v)),
    )
    closeApproverList()
  }

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor))

  const handleDragStart = useCallback(() => {
    setIsMoving(false)
  }, [])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (active.id !== over?.id) {
      setFlow((values) => {
        const oldIndex = values.map((v) => v.item_id).indexOf(Number(active.id))
        const newIndex = values.map((v) => v.item_id).indexOf(Number(over?.id))

        return arrayMove(values, oldIndex, newIndex).map((v, i) => ({ ...v, priority: values.length - (i + 1) }))
      })
    }
    setIsMoving(true)
  }, [])

  const handleDragCancel = useCallback(() => {
    setIsMoving(true)
  }, [])

  const buildApprovalFlow = (list: Flow[]) => {
    if (!list.length) {
      return (
        <>
          承認フローが設定されていません
          <button
            type="button"
            style={{
              fontSize: "20px",
              padding: 0,
              color: "#039831",
            }}
            onClick={addFlow}
          >
            <FontAwesomeIcon icon={['fas', 'plus-circle']} />
          </button>
        </>
      )
    }
    return (
      <>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext items={list.map((v) => v.item_id.toString())} strategy={rectSortingStrategy}>
            <Grid columns={4}>
              {
                list.map((member, i) => (
                  <div
                    key={`approver_${member.item_id}`}
                    className="approver_block_wrapper"
                  >
                    {
                      isMoving && (
                        <>
                          <button
                            type="button"
                            className="delete_approver"
                            onClick={() => delFlow(member.item_id)}
                          >
                            <FontAwesomeIcon icon={['fas', 'times']} />
                          </button>
                          <div
                            onClick={() => setDisplaySelectBoxNum(member.item_id)}
                            role="none"
                            className="approver_select_box_wrapper"
                          >
                            {
                              member.member_id ? (
                                <>
                                  <img
                                    alt="profile"
                                    // eslint-disable-next-line max-len
                                    src={member.profile_image ? member.profile_image : `/static/images/profile/${(Number(member.member_id) || 0) % 12}.jpeg`}
                                    className="profile_icon"
                                    style={{
                                      width: "20px",
                                      height: "20px",
                                      borderRadius: "50%",
                                    }}
                                  />
                                  <ruby
                                    style={{
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {member.name}
                                    <rt>{member.kana}</rt>
                                  </ruby>
                                </>
                              ) : (
                                <>
                                  選択してください
                                </>
                              )
                            }
                          </div>
                        </>
                      )
                    }
                    {
                      displaySelectBoxNum === member.item_id && (
                        <>
                          <div
                            style={{
                              backgroundColor: "#FFFFFF",
                              position: "absolute",
                              top: "50px",
                              zIndex: "2004",
                              borderRadius: "4px",
                              padding: "16px",
                              border: "solid 1px #CCCCCC",
                            }}
                          >
                            <div
                              style={{
                                padding: "4px",
                                fontWeight: "bold",
                                textAlign: "center",
                              }}
                            >
                              承認者設定
                            </div>
                            <div>
                              <input
                                type="search"
                                placeholder="検索キーワードを入力"
                                onChange={(e) => setSearchMemberWord(e.target.value)}
                                style={{
                                  border: "solid 1px #CCCCCC",
                                  width: "100%",
                                  padding: "8px",
                                  outline: "unset",
                                  marginBottom: "8px",
                                }}
                              />
                            </div>
                            <div
                              style={{
                                padding: "4px",
                                fontWeight: "bold",
                              }}
                            >
                              申請者
                            </div>
                            <div
                              style={{
                                border: "solid 1px #CCCCCC",
                                borderRadius: "4px",
                                width: "350px",
                                maxHeight: "250px",
                                overflowY: "scroll",
                                padding: "8px",
                                display: "flex",
                                flexWrap: "wrap",
                                gap: "4px",
                              }}
                            >
                              {
                                !filterMemberList.length && (
                                  <>
                                    対象の申請者がいません
                                  </>
                                )
                              }
                              {
                                filterMemberList.map((v) => (
                                  <div
                                    key={`select_${member.item_id}_${v.member_id}`}
                                    onClick={() => selectApprover(v.member_id, member.item_id)}
                                    role="none"
                                    style={{
                                      cursor: "pointer",
                                      padding: "4px 8px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "left",
                                      gap: "4px",
                                      border: "solid 1px #CCCCCC",
                                      borderRadius: "50px",
                                      backgroundColor: v.member_id === member.member_id ? "#f3fff3" : "#FFFFFF",
                                    }}
                                  >
                                    <img
                                      alt="profile"
                                      src={v.profile_image ? v.profile_image : `/static/images/profile/${(Number(v.member_id) || 0) % 12}.jpeg`}
                                      className="profile_icon"
                                      style={{
                                        width: "24px",
                                        height: "24px",
                                        borderRadius: "50%",
                                      }}
                                    />
                                    <ruby
                                      style={{
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {v.name}
                                      <rt>{v.kana}</rt>
                                    </ruby>
                                  </div>
                                ))
                              }
                            </div>
                          </div>
                          <div className="close_screen" role="presentation" onClick={() => closeApproverList()} />
                        </>
                      )
                    }
                    <SortableItem id={member.item_id.toString()}>
                      <div className="approver_block">
                        <div className="approver_block_title">
                          <div className="display_icon">
                            <FontAwesomeIcon icon={['fas', 'grip-vertical']} />
                          </div>
                          <div>
                            {list.length === i + 1 ? "最終" : `${i + 1}次`}
                            承認者
                          </div>
                        </div>
                        <div className="approver_block_main">
                          {
                            isMoving || (
                              <>
                                {
                                  member.member_id ? (
                                    <>
                                      <img
                                        alt="profile"
                                        // eslint-disable-next-line max-len
                                        src={member.profile_image ? member.profile_image : `/static/images/profile/${(Number(member.member_id) || 0) % 12}.jpeg`}
                                        className="profile_icon"
                                      />
                                      <ruby
                                        style={{
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {member.name}
                                        <rt>{member.kana}</rt>
                                      </ruby>
                                    </>
                                  ) : (
                                    <>
                                      選択してください
                                    </>
                                  )
                                }
                              </>
                            )
                          }
                        </div>
                      </div>
                      <FontAwesomeIcon icon={['fas', 'arrow-right']} />
                    </SortableItem>
                  </div>
                ))
              }
              <button
                type="button"
                style={{
                  fontSize: "20px",
                  padding: 0,
                  color: "#039831",
                }}
                onClick={addFlow}
              >
                <FontAwesomeIcon icon={['fas', 'plus-circle']} />
              </button>
            </Grid>
          </SortableContext>
        </DndContext>
      </>
    )
  }

  const validationParams = () => {
    const tmpErrors: Errors = initErrors
    let errorFlg = false

    const ApproverIds = flow.map((v) => v.member_id).filter((v) => v)
    if (selectMembers.some((v) => ApproverIds.includes(v.member_id))) {
      const diffMembers = selectMembers.filter((v) => ApproverIds.includes(v.member_id))
      setApproverMembers(`${diffMembers.map((v) => v.name).join(",")}`)
    } else {
      setApproverMembers("")
    }
    const duplicationIds = Array.from(
      new Set(flow.map((v) => v.member_id).filter((v) => v).filter((v, _i, self) => (self.indexOf(v) !== self.lastIndexOf(v)))),
    )
    if (duplicationIds.length) {
      const duplicationMembers = modalState.memberList.filter((v) => duplicationIds.includes(v.member_id))
      tmpErrors.flow.push(`承認者が重複しています%br%対象メンバー:${duplicationMembers.map((v) => v.name).join(",")}`)
      errorFlg = true
    }
    if (flow.filter((v) => !v.member_id).length) {
      tmpErrors.flow.push("承認者を選択してください")
      errorFlg = true
    }
    if (errorFlg) {
      setErrors({
        name: tmpErrors.name,
        flow: tmpErrors.flow,
        member: tmpErrors.member,
      })
      return
    }
    setErrors(initErrors)
  }

  const checkOtherAssignedMember = () => {
    if (selectMembers.filter((v) => v.member_id).some((v) => modalState.alreadyAssignedMemberIds.includes(v.member_id))) {
      setOtherAssignedMembers(selectMembers.filter((v) => modalState.alreadyAssignedMemberIds.includes(v.member_id)))
    } else {
      setOtherAssignedMembers([])
    }
  }

  useEffect(() => {
    if (!modalState) return
    const { targetData } = modalState
    setGroupName(targetData?.name || initialInput.name)
    setSelectMembers(targetData?.members || [])
    setErrors(initErrors)
  }, [modalState])

  useEffect(() => {
    setDisplayModal(modalState.display)
    setFlow(sortBy(modalState.targetData?.approvers.map((v, i) => ({ ...v, item_id: i })), "priority").reverse())
    setOtherAssignedMembers([])
    closeApproverList()
  }, [modalState.display])

  useEffect(() => {
    validationParams()
    checkOtherAssignedMember()
  }, [flow, selectMembers])

  return (
    <>
      <Modal
        title={modalState.type === "create" ? "承認グループの作成" : "承認グループの編集"}
        show={displayModal}
        wrapperClass="approver_setting_modal w750"
        closeCallback={clear}
        closeable
      >
        <>
          <div className="description">
            <div>
              承認グループの詳細を設定します。
            </div>
            <div>
              承認者を変更すると、申請者全員の承認完了データ以外はすべて未申請状態になります。
            </div>
            <div>
              また、申請者を変更したとき、対象メンバーの承認完了データ以外はすべて未申請状態になります。
            </div>
          </div>
          <div className="item">
            <div className="title flex">
              <div className="label red">必須</div>
              承認グループ名
            </div>
            <div className="detail row_1">
              <input
                type="text"
                placeholder="承認グループの名称を入力してください"
                onChange={(event) => setGroupName(event.target.value)}
                onBlur={(event) => replaceAndStripName(event)}
                value={groupName}
              />
            </div>
            {
              errors.name && errors.name.map((error) => (
                <div
                  className="error"
                  key={error}
                >
                  <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} style={{ fontSize: "20px" }} />
                  {/* eslint-disable-next-line react/no-danger */}
                  <div dangerouslySetInnerHTML={{ __html: error.replace('%br%', '<br />') }} />
                </div>
              ))
            }
          </div>
          <div className="item">
            <div className="title">
              承認フロー
            </div>
            <fieldset className="approver_area_wrapper">
              <div className="approver_area">
                {buildApprovalFlow(flow)}
              </div>
            </fieldset>
            {
              errors.flow && errors.flow.map((error) => (
                <div
                  className="error"
                  key={error}
                >
                  <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} style={{ fontSize: "20px" }} />
                  {/* eslint-disable-next-line react/no-danger */}
                  <div dangerouslySetInnerHTML={{ __html: error.replace('%br%', '<br />') }} />
                </div>
              ))
            }
          </div>
          <div className="item">
            <div className="title">
              申請者
            </div>
            <fieldset className="member_area_wrapper">
              <div className="member_area">
                {
                  selectMembers.map((value) => (
                    <div
                      className="member_block"
                      key={`condition_${value.member_id}`}
                    >
                      {
                        value.profile_image ? (
                          <img
                            alt="profile"
                            src={value.profile_image}
                            className="profile_icon"
                          />
                        ) : (
                          <img
                            alt=""
                            src={`/static/images/profile/${(Number(value.member_id) || 0) % 12}.jpeg`}
                            className="profile_icon"
                          />
                        )
                      }
                      {value.name}
                      <span
                        style={{
                          cursor: "pointer",
                        }}
                        onClick={() => delMember(value.member_id)}
                        role="none"
                      >
                        <FontAwesomeIcon icon={['fas', 'times']} />
                      </span>
                    </div>
                  ))
                }
                <AddMemberModule
                  memberIdList={selectMembers.map((v) => v.member_id)}
                  callbackMembers={(val) => setMember([...modalState.memberList.filter((v) => val.map((w) => w.id).includes(v.member_id))])}
                  memberList={modalState.memberList.map((v) => ({ id: v.member_id, name: v.name, profile_image: v.profile_image }))}
                />
              </div>
            </fieldset>
            {
              !!otherAssignedMembers.length && (
                <div className="info_area">
                  <FontAwesomeIcon icon={['far', 'lightbulb']} style={{ fontSize: "20px", color: "#babd24", margin: "0 4px" }} />
                  <div>
                    <div>
                      対象の申請者を他の承認グループからこの承認グループに移動します
                    </div>
                    <div>
                      {`対象申請者:${otherAssignedMembers.map((v) => v.name).join(",")}`}
                    </div>
                  </div>
                </div>
              )
            }
            {
              !!approverMembers && (
                <div className="info_area">
                  <FontAwesomeIcon icon={['far', 'lightbulb']} style={{ fontSize: "20px", color: "#babd24", margin: "0 4px" }} />
                  <div>
                    <div>
                      承認者が対象申請者に設定されています
                    </div>
                    <div>
                      ご注意ください
                    </div>
                    <div>
                      {`対象申請者:${approverMembers}`}
                    </div>
                  </div>
                </div>
              )
            }
            {
              errors.member && errors.member.map((error) => (
                <div
                  className="error"
                  key={error}
                >
                  <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} style={{ fontSize: "20px" }} />
                  {/* eslint-disable-next-line react/no-danger */}
                  <div dangerouslySetInnerHTML={{ __html: error.replace('%br%', '<br />') }} />
                </div>
              ))
            }
          </div>
        </>
        <>
          <button type="button" className="button cancel" onClick={clear}>
            {t('form.component.cancel')}
          </button>
          <button
            type="button"
            className="button"
            onClick={() => setIsDisplayConfirmModal(true)}
            disabled={!!errors.name.length || !!errors.flow.length || !!errors.member.length}
          >
            { modalState.type === 'create' ? t('form.component.create') : t('form.component.modify') }
          </button>
        </>
      </Modal>
      <Modal
        show={isDisplayConfirmModal}
        wrapperClass="approver_setting_modal w900"
      >
        <>
          <div
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              marginBottom: "16px",
            }}
          >
            変更確認
          </div>
          <div
            className="description"
            style={{
              fontSize: "14px",
              color: "#0f6f02",
            }}
          >
            <div>
              承認者を変更すると、申請者全員の承認完了データ以外はすべて未申請状態になります。
            </div>
            <div>
              また、申請者を変更したとき、対象申請者の承認完了データ以外はすべて未申請状態になります。
            </div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "anchor-center",
              gap: "8px",
              padding: "8px",
              border: "solid 1px #008800",
              borderRadius: "4px",
              backgroundColor: "#e7fce7",
              color: isConfirm ? "#008800" : "#333333",
              cursor: "pointer",
            }}
            onClick={() => setIsConfirm(!isConfirm)}
            role="none"
          >
            <FontAwesomeIcon icon={['far', isConfirm ? 'check-square' : 'square']} style={{ fontSize: "18px" }} />
            上記内容を確認しました
          </div>
        </>
        <>
          <button type="button" className="button cancel" onClick={() => setIsDisplayConfirmModal(false)}>
            {t('form.component.cancel')}
          </button>
          <button
            type="button"
            className="button"
            onClick={saveApprovalGroup}
            disabled={!isConfirm}
          >
            同意して変更
          </button>
        </>
      </Modal>
    </>
  )
}

export default ApproverSettingModal
